import { RobotOutlined } from '@ant-design/icons'
import {
  faChartLine,
  faHeartPulse,
  faInfoCircle,
  faPlus,
  faProcedures,
  faRefresh,
  faVial,
  faFilter,
  faEllipsisVertical,
  faChain
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Divider, Space, Tooltip, theme, Card, Dropdown } from 'antd'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import WebRTCAssistant from '../../../P AI Voice Assistant/lib/components/WebRTCAssistant'
import { getExaminationContext } from '../AI Assistant/config/aiAssistantContexts'
import { getExaminationTools } from '../AI Assistant/utils/examinationAITools'

const AssessmentActions = ({
  fetchPatientData,
  connectionStatus,
  setCaptureModalOpen,
  setLinktopModalOpen,
  patientData,
  bookingData,
  patientDataEntries,
  visibleSections,
  toggleSectionVisibility,
  setFilterPanelVisible
}) => {
  const { token } = theme.useToken()
  const { loading: dataPointsLoading } = useSelector((state) => state.dataPoints)
  const { loading: templatesLoading } = useSelector((state) => state.examTemplates)
  const [aiContext, setAiContext] = useState('')

  const isLoading = dataPointsLoading || templatesLoading

  const buttonStyles = {
    actionButton: {
      boxShadow: 'none',
      borderRadius: 6,
      padding: '0 16px',
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500
    },
    primaryButton: {
      background: token.colorPrimary,
      color: 'white'
    },
    secondaryButton: {
      background: '#F0F5FF',
      color: token.colorPrimary,
      border: 'none'
    },
    sync: {
      background: token.colorSuccess,
      color: 'white'
    },
    realtimeStatus: {
      padding: '4px 12px',
      borderRadius: 8,
      alignItems: 'center',
      background: connectionStatus.background
    },
    actionsCard: {
      borderRadius: 12,
      marginTop: 12,
      marginBottom: 16
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12
    },
    primaryActions: {
      display: 'flex',
      gap: 12,
      flexWrap: 'wrap'
    },
    secondaryActions: {
      display: 'flex',
      gap: 8
    },
    infoIcon: {
      marginLeft: 8,
      fontSize: 14,
      color: token.colorTextSecondary,
      cursor: 'help'
    }
  }

  const calculateAge = (dateOfBirth) => {
    return dayjs().diff(dayjs(dateOfBirth), 'year')
  }

  // Enhanced function to extract vital signs from patient data entries
  const extractVitalSigns = () => {
    if (!patientDataEntries || !patientDataEntries.length) {
      return []
    }

    // Common vital sign data point names - expanded list with variations
    const vitalSignNames = [
      'Blood Pressure',
      'BP',
      'Systolic',
      'Diastolic',
      'bloodPressure',
      'Heart Rate',
      'HR',
      'Pulse',
      'heartRate',
      'Temperature',
      'Temp',
      'temperature',
      'Respiratory Rate',
      'RR',
      'Respiration',
      'respiratoryRate',
      'Oxygen Saturation',
      'SpO2',
      'O2 Sat',
      'Oxygen',
      'oxygenSaturation',
      'Weight',
      'weight',
      'Height',
      'height',
      'BMI',
      'bmi'
    ]

    // More flexible matching for vital signs
    const extractedVitals = patientDataEntries
      .filter((entry) => {
        // Check if entry has dataPointName property
        if (!entry) {
          return false
        }

        // Check for blood pressure in checkinData
        if (entry.checkinData && entry.checkinData.bloodPressure) {
          return true
        }

        // Check for bloodPressure key
        if (entry.key === 'bloodPressure') {
          return true
        }

        // Check if entry has dataPointName property
        if (!entry.dataPointName) {
          return false
        }

        // Check if the dataPointName matches any of the vital sign names
        const match = vitalSignNames.some((name) => {
          const entryName = entry.dataPointName.toLowerCase()
          return entryName.includes(name.toLowerCase())
        })

        return match
      })
      .map((entry) => {
        // Log each matched vital sign

        // Check for blood pressure in checkinData
        if (entry.checkinData && entry.checkinData.bloodPressure) {
          const { systolic, diastolic } = entry.checkinData.bloodPressure

          return {
            name: 'Blood Pressure',
            value: `${systolic || '?'}/${diastolic || '?'}`,
            unit: 'mmHg',
            rawData: {
              systolic,
              diastolic
            }
          }
        }

        // Check for bloodPressure key
        if (entry.key === 'bloodPressure') {
          // Handle different formats of blood pressure data
          if (entry.value) {
            if (Array.isArray(entry.value)) {
              // Format: [systolic, diastolic] or [{value: systolic}, {value: diastolic}]
              return {
                name: 'Blood Pressure',
                value: `${entry.value[0]?.value || entry.value[0] || '?'}/${
                  entry.value[1]?.value || entry.value[1] || '?'
                }`,
                unit: entry.unit || 'mmHg',
                rawData: {
                  systolic: entry.value[0]?.value || entry.value[0],
                  diastolic: entry.value[1]?.value || entry.value[1]
                }
              }
            } else if (typeof entry.value === 'object') {
              // Format: {systolic: value, diastolic: value}
              return {
                name: 'Blood Pressure',
                value: `${entry.value.systolic || '?'}/${entry.value.diastolic || '?'}`,
                unit: entry.unit || 'mmHg',
                rawData: {
                  systolic: entry.value.systolic,
                  diastolic: entry.value.diastolic
                }
              }
            } else if (typeof entry.value === 'string' && entry.value.includes('/')) {
              // Format: "systolic/diastolic"
              const [systolic, diastolic] = entry.value.split('/').map((v) => parseInt(v.trim(), 10))
              return {
                name: 'Blood Pressure',
                value: `${systolic || '?'}/${diastolic || '?'}`,
                unit: entry.unit || 'mmHg',
                rawData: {
                  systolic,
                  diastolic
                }
              }
            }
          }
        }

        // Special handling for blood pressure which has systolic and diastolic values
        if (
          entry.dataPointName &&
          (entry.dataPointName.toLowerCase().includes('blood pressure') ||
            entry.dataPointName.toLowerCase().includes('bp'))
        ) {
          // Check if blood pressure is already in the correct format
          if (entry.value && typeof entry.value === 'object' && Array.isArray(entry.value)) {
            // Already in array format, ensure it has the right structure
            return {
              name: 'Blood Pressure',
              value: `${entry.value[0]?.value || entry.value[0] || '?'}/${
                entry.value[1]?.value || entry.value[1] || '?'
              }`,
              unit: entry.unit || 'mmHg',
              rawData: {
                systolic: entry.value[0]?.value || entry.value[0],
                diastolic: entry.value[1]?.value || entry.value[1]
              }
            }
          }
          // Check if blood pressure is in object format with systolic/diastolic properties
          else if (
            entry.value &&
            typeof entry.value === 'object' &&
            (entry.value.systolic !== undefined || entry.value.diastolic !== undefined)
          ) {
            return {
              name: 'Blood Pressure',
              value: `${entry.value.systolic || '?'}/${entry.value.diastolic || '?'}`,
              unit: entry.unit || 'mmHg',
              rawData: {
                systolic: entry.value.systolic,
                diastolic: entry.value.diastolic
              }
            }
          }
          // Check if blood pressure is in string format like "120/80"
          else if (entry.value && typeof entry.value === 'string' && entry.value.includes('/')) {
            const [systolic, diastolic] = entry.value.split('/').map((v) => parseInt(v.trim(), 10))
            return {
              name: 'Blood Pressure',
              value: `${systolic || '?'}/${diastolic || '?'}`,
              unit: entry.unit || 'mmHg',
              rawData: {
                systolic,
                diastolic
              }
            }
          }
        }

        // Default handling for other vital signs
        return {
          name: entry.dataPointName || entry.key,
          value: entry.value,
          unit: entry.unit || ''
        }
      })

    if (extractedVitals.length > 0) {
    } else {
    }

    return extractedVitals
  }

  // Create patient details object
  useEffect(() => {
    if (patientData) {
      // Extract vital signs first to ensure they're available for logging
      const vitalSigns = extractVitalSigns()

      // Log blood pressure specifically for debugging
      const bloodPressureReading = vitalSigns.find((vital) => vital.name.toLowerCase().includes('blood pressure'))

      if (bloodPressureReading) {
      } else {
        // Check if blood pressure is in the booking data
        if (bookingData && bookingData.checkinData && bookingData.checkinData.bloodPressure) {
          // Add blood pressure from booking data
          const { systolic, diastolic } = bookingData.checkinData.bloodPressure
          if (systolic && diastolic) {
            const bpVital = {
              name: 'Blood Pressure',
              value: `${systolic}/${diastolic}`,
              unit: 'mmHg',
              rawData: {
                systolic,
                diastolic
              }
            }

            vitalSigns.push(bpVital)
          }
        } else {
        }
      }

      const details = {
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        age: patientData.dateOfBirth ? calculateAge(patientData.dateOfBirth) : null,
        gender: patientData.gender || 'Unknown',
        vitalSigns: vitalSigns,
        symptoms: bookingData?.symptoms || [],
        labTests:
          bookingData?.careSyncTests?.map((testId) => ({
            id: testId,
            name: testId // Ideally this would be the test name, not just the ID
          })) || []
      }

      const contextObj = getExaminationContext(details)

      setAiContext(contextObj.context)
    }
    // eslint-disable-next-line
  }, [patientData, patientDataEntries, bookingData])

  const handleAIAction = (action) => {
    // Handle specific AI actions here
    if (action && action.name) {
      switch (action.name) {
        case 'captureVitals':
          // Handle capturing vital signs
          break

        case 'captureBloodPressure':
          // Extract systolic and diastolic values
          const { systolic, diastolic } = action.parameters

          if (systolic && diastolic) {
            // Here you would typically save this to your patient data
            // This is where you would integrate with your data saving mechanism
          } else {
            console.warn('Invalid blood pressure values provided')
          }
          break

        case 'recordSymptom':
          // Handle recording symptoms
          break

        case 'generateReport':
          // Handle report generation
          break

        case 'suggestTests':
          // Handle test suggestions
          break

        default:
      }
    }
  }

  const handleAIWarning = (warning) => {}

  // Create a default context for when patient data is not available
  const defaultContext = `You are an AI Medical Assistant designed to help healthcare professionals during patient examinations.
  
  Your capabilities include:
  - Capturing vital signs
  - Recording symptoms
  - Suggesting tests based on symptoms
  - Generating medical reports
  - Providing clinical decision support
  
  Please use appropriate medical terminology and maintain patient confidentiality.
  Alert medical professionals of any concerning findings.
  
  Initial greeting: "Hello! I'm your AI Medical Assistant. How can I help you today?"`

  // More actions dropdown menu items
  const moreActionsItems = [
    // Remove the linktop item from here
  ]

  return (
    <Card sizes='small' style={buttonStyles.actionsCard}>
      <div style={buttonStyles.actionBar}>
        <div style={buttonStyles.primaryActions}>
          <Button
            disabled={isLoading}
            type='primary'
            onClick={() => setCaptureModalOpen(true)}
            icon={<FontAwesomeIcon icon={faPlus} />}
            style={{ ...buttonStyles.actionButton, ...buttonStyles.primaryButton }}
          >
            New Capture
          </Button>

          <Button
            onClick={fetchPatientData}
            type='primary'
            style={{ ...buttonStyles.actionButton, ...buttonStyles.primaryButton }}
            icon={<FontAwesomeIcon icon={faRefresh} />}
          >
            Sync
          </Button>

          <Button
            onClick={() => setFilterPanelVisible(true)}
            type='default'
            style={{ ...buttonStyles.actionButton, ...buttonStyles.secondaryButton }}
            icon={<FontAwesomeIcon icon={faFilter} x />}
          >
            Filter View
          </Button>

          <Button
            onClick={() => setLinktopModalOpen(true)}
            type='default'
            style={{ ...buttonStyles.actionButton, ...buttonStyles.secondaryButton }}
            icon={<FontAwesomeIcon icon={faChain} />}
          >
            Connect Device
          </Button>
        </div>

        <Space>
          <Tooltip title={connectionStatus.title}>
            <Button style={buttonStyles.realtimeStatus}>
              <small>Real-Time Updates</small>
              <FontAwesomeIcon
                icon={connectionStatus.icon}
                size='sm'
                color={connectionStatus.color}
                style={{ cursor: 'help' }}
              />
            </Button>
          </Tooltip>

          {moreActionsItems.length > 0 && (
            <Dropdown
              menu={{
                items: moreActionsItems
              }}
              placement='bottomRight'
              arrow
            >
              <Button type='text' icon={<FontAwesomeIcon icon={faEllipsisVertical} />} style={{ marginLeft: 4 }} />
            </Dropdown>
          )}

          {/* <WebRTCAssistant
            systemContext={aiContext || defaultContext}
            customTools={getExaminationTools()}
            onAction={handleAIAction}
            onWarning={handleAIWarning}
            buttonProps={{
              type: 'text',
              icon: <RobotOutlined />,
              style: { color: token.colorPrimary }
            }}
          /> */}
        </Space>
      </div>
    </Card>
  )
}

AssessmentActions.propTypes = {
  fetchPatientData: PropTypes.func.isRequired,
  connectionStatus: PropTypes.shape({
    title: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    icon: PropTypes.object.isRequired,
    color: PropTypes.string.isRequired
  }).isRequired,
  setCaptureModalOpen: PropTypes.func.isRequired,
  setLinktopModalOpen: PropTypes.func.isRequired,
  patientData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dateOfBirth: PropTypes.string,
    gender: PropTypes.string
  }),
  bookingData: PropTypes.object,
  patientDataEntries: PropTypes.array,
  visibleSections: PropTypes.shape({
    vitalSigns: PropTypes.bool.isRequired,
    generalStats: PropTypes.bool.isRequired,
    bloodTests: PropTypes.bool.isRequired,
    procedures: PropTypes.bool.isRequired
  }).isRequired,
  toggleSectionVisibility: PropTypes.func.isRequired,
  setFilterPanelVisible: PropTypes.func.isRequired
}

export default memo(AssessmentActions)
